import React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {C52_PAGE} from '@/pages/components/audit-program/c-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface C52QuestionnaireViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const RELEVANT_OPTIONS = ['', 'Y', 'N', 'N/A'] as const;

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.7rem',
    py: 0.75,
    px: 0.4,
    lineHeight: 1.25,
    verticalAlign: 'bottom' as const,
    whiteSpace: 'pre-line' as const,
};
const bodySx = {
    ...cellBorder,
    py: 0.4,
    px: 0.4,
    verticalAlign: 'top' as const,
    overflow: 'hidden',
};

const nativeBaseSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 0.75,
    py: '4px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.25,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    '&:hover': {borderColor: 'text.primary'},
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
};

function CellInput({
    value,
    onChange,
    align = 'left',
}: {
    value: string;
    onChange: (v: string) => void;
    align?: 'left' | 'center';
}) {
    return (
        <Box
            component="input"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={{...nativeBaseSx, textAlign: align}}
        />
    );
}

function RelevantSelect({value, onChange}: {value: string; onChange: (v: string) => void}) {
    const normalized = RELEVANT_OPTIONS.includes(value as (typeof RELEVANT_OPTIONS)[number])
        ? value
        : value; // keep legacy Yes/No if present
    return (
        <Box
            component="select"
            value={normalized}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
            sx={{
                ...nativeBaseSx,
                textAlign: 'center',
                cursor: 'pointer',
                pr: 2.5,
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: (theme: Theme) =>
                    `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(
                        `<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${theme.palette.text.secondary}' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>`,
                    )}")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 6px center',
            }}
        >
            {RELEVANT_OPTIONS.map((opt) => (
                <option key={opt || '_'} value={opt}>
                    {opt || '—'}
                </option>
            ))}
            {/* preserve unexpected saved values (e.g. Yes/No from older UI) */}
            {normalized && !RELEVANT_OPTIONS.includes(normalized as (typeof RELEVANT_OPTIONS)[number]) ? (
                <option value={normalized}>{normalized}</option>
            ) : null}
        </Box>
    );
}

/** Excel C5.2 — Internal Control Questionnaire */
export default function C52QuestionnaireView({header, values, onChange}: C52QuestionnaireViewProps) {
    const f = (id: string, k: string) => {
        const direct = values[`${id}.${k}`];
        if (direct != null && String(direct).trim() !== '') return direct;
        // migrate old checklist defaults that stored Y/N/N/A in comments
        if (k === 'relevant') return values[`${id}.comments`] ?? '';
        if (k === 'refControl') return values[`${id}.wpRef`] ?? '';
        return '';
    };

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode="C5.2"
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1, mt: 0.5}}
            >
                {C52_PAGE.title}
            </Typography>

            {C52_PAGE.intros.map((intro, idx) => (
                <Typography
                    key={idx}
                    variant="body2"
                    sx={{mb: 1.25, lineHeight: 1.45, whiteSpace: 'pre-line', fontStyle: 'italic'}}
                >
                    {intro}
                </Typography>
            ))}

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table
                    size="small"
                    sx={{
                        width: '100%',
                        minWidth: 960,
                        tableLayout: 'fixed',
                        borderCollapse: 'collapse',
                    }}
                >
                    <colgroup>
                        <col style={{width: '4%'}}/>
                        <col style={{width: '52%'}}/>
                        <col style={{width: '12%'}}/>
                        <col style={{width: '16%'}}/>
                        <col style={{width: '16%'}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} colSpan={2}/>
                            <TableCell sx={headSx} align="center">
                                {C52_PAGE.columns.relevant}
                            </TableCell>
                            <TableCell sx={headSx} align="center">
                                {C52_PAGE.columns.refControl}
                            </TableCell>
                            <TableCell sx={headSx} align="center">
                                {C52_PAGE.columns.refWeakness}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {C52_PAGE.rows.map((row) => {
                            if (row.kind === 'section') {
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={bodySx} colSpan={5}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={700}
                                                sx={{py: 0.25, fontStyle: 'italic'}}
                                            >
                                                {row.text}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            }
                            return (
                                <TableRow key={row.id}>
                                    <TableCell sx={bodySx}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {row.num}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={bodySx}>
                                        <Typography variant="body2" sx={{lineHeight: 1.4}}>
                                            {row.text}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={bodySx}>
                                        <RelevantSelect
                                            value={f(row.id, 'relevant')}
                                            onChange={(v) => onChange(`${row.id}.relevant`, v)}
                                        />
                                    </TableCell>
                                    <TableCell sx={bodySx}>
                                        <CellInput
                                            value={f(row.id, 'refControl')}
                                            onChange={(v) => onChange(`${row.id}.refControl`, v)}
                                            align="center"
                                        />
                                    </TableCell>
                                    <TableCell sx={bodySx}>
                                        <CellInput
                                            value={f(row.id, 'refWeakness')}
                                            onChange={(v) => onChange(`${row.id}.refWeakness`, v)}
                                            align="center"
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
